import { Formik } from 'formik';

import { isMobile } from 'hooks';
import { getCheckChange, getCheckChangeLineId } from 'utils/cart';
import { ButtonColors, PaymentMethodType } from 'types';
import {
  Row,
  Col,
  Icon,
  Button,
  DialogType,
  InputField,
  ButtonTypes,
  CommonStyles,
} from 'components';

import { useRemainderCart } from '../hooks';
import { TemplateDialog } from '../template-dialog';
import { FullAmountButton } from '../full-amount-button';

import { ChangeDue } from './change-due';
import { CheckProps, CheckFormValues } from './types';
import { initialValues, getValidationSchema } from './config';

export const Check = ({
  amount,
  onAddPaymentMethod,
  isProcessingFeeEnabled,
}: CheckProps) => {
  const mobile = isMobile();
  // Same balance as the Cash tile (card processing fees count as paid), so
  // the change on a check over the amount due is exact.
  const { remainder, isBalanceZero } = useRemainderCart(
    amount,
    isProcessingFeeEnabled
  );

  return (
    <TemplateDialog title="Check" icon={<Icon.CheckCard />}>
      {(close, _, ButtonWrap) => (
        <Formik<CheckFormValues>
          initialValues={initialValues}
          validationSchema={getValidationSchema(remainder)}
          onSubmit={(values) => {
            console.log('[Checkout] added check in payment method', values);

            const checkAmount = +values.amount;
            const type = PaymentMethodType.Check;
            const id = `${values.amount}-${type}`;
            const change = getCheckChange(checkAmount, remainder);
            onAddPaymentMethod({
              ...values,
              id,
              type,
              amount: checkAmount,
              totalAmount: checkAmount,
              checkNumber: +values.checkNumber,
            });
            // Owner 19 Oct 2026: the check stays whole (it goes to the bank);
            // the change handed back is cash out of the register, recorded
            // like checkout 21070: a cash line of minus the change.
            if (change > 0) {
              onAddPaymentMethod({
                amount: -change,
                totalAmount: -change,
                receivedCashAmount: 0,
                type: PaymentMethodType.Cash,
                id: getCheckChangeLineId(id),
              });
            }
            close();
          }}
        >
          {({ dirty, isSubmitting }) => (
            <CommonStyles.Form>
              <Row>
                <Col>
                  <InputField
                    number
                    decimal
                    name="amount"
                    label="Amount"
                    placeholder="Amount"
                  />

                  <FullAmountButton<CheckFormValues>
                    remainder={remainder}
                    isBalanceZero={isBalanceZero}
                  />

                  <ChangeDue remainder={remainder} />
                </Col>
              </Row>

              <Row>
                <Col>
                  <InputField
                    name="holderName"
                    label="Check holder name"
                    placeholder="Check holder name"
                  />
                </Col>
              </Row>

              <Row>
                <Col col={6}>
                  <InputField
                    number
                    name="checkNumber"
                    label="Check number"
                    placeholder="Check number"
                  />
                </Col>
                <Col col={6}>
                  <InputField
                    type="tel"
                    name="phoneNumber"
                    label="Phone number"
                    placeholder="Phone number"
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <InputField
                    name="driverLicense"
                    label="Driver license/ID"
                    placeholder="Driver license/ID"
                  />
                </Col>
              </Row>

              <ButtonWrap type={DialogType.Plain}>
                <Button
                  text="Cancel"
                  onClick={close}
                  fullWidth={mobile}
                  colorType={ButtonColors.LightBlue}
                />
                <Button
                  text="Confirm"
                  fullWidth={mobile}
                  type={ButtonTypes.Submit}
                  disabled={!dirty || isSubmitting}
                />
              </ButtonWrap>
            </CommonStyles.Form>
          )}
        </Formik>
      )}
    </TemplateDialog>
  );
};
